import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import OneSignalNative, { type PushSubscriptionChangedState } from '@onesignal/capacitor-plugin';
import { OneSignal as OneSignalWeb, SubscriptionChangeEvent } from 'onesignal-ngx';
import { environment } from '../../../environments/environment';

function createUUID(): string {
  let dt = new Date().getTime();
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private http = inject(HttpClient);
  private webOneSignal = inject(OneSignalWeb);

  private getUUID(): string {
    let uuid = localStorage.getItem('uuid');
    if (!uuid) {
      uuid = createUUID();
      localStorage.setItem('uuid', uuid);
    }
    return uuid;
  }

  private webPushReady = false;

  async initializeWeb(): Promise<void> {
    if (!this.webOneSignal.Notifications.isPushSupported()) return;
    try {
      await this.webOneSignal.init({
        appId: environment.ONE_SIGNAL_APP_ID,
        serviceWorkerPath: 'OneSignalSDKWorker.js',
      });
      this.webPushReady = true;
      await this.webOneSignal.Notifications.requestPermission();
    } catch {
      // App ID not configured for web push in OneSignal dashboard — silently skip
    }
  }

  async registerPushToken(accountId: number, sessionId: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      const [userId, pushToken] = await Promise.all([
        OneSignalNative.User.pushSubscription.getIdAsync(),
        OneSignalNative.User.pushSubscription.getTokenAsync(),
      ]);

      if (userId && pushToken) {
        this.sendToken(userId, pushToken, accountId, sessionId);
      } else {
        // Token pas encore disponible (APNs en cours d'enregistrement ou permission en attente)
        // On attend l'événement change pour l'obtenir
        const listener = (event: PushSubscriptionChangedState) => {
          const { id, token } = event.current;
          if (id && token) {
            OneSignalNative.User.pushSubscription.removeEventListener('change', listener);
            this.sendToken(id, token, accountId, sessionId);
          }
        };
        OneSignalNative.User.pushSubscription.addEventListener('change', listener);
      }
    } else {
      if (!this.webPushReady) return;
      const userId = this.webOneSignal.User.PushSubscription.id ?? null;
      const pushToken = this.webOneSignal.User.PushSubscription.token ?? null;

      if (userId && pushToken) {
        this.sendToken(userId, pushToken, accountId, sessionId);
      } else {
        // Subscription not ready yet (permission just granted) — wait for it
        const listener = (event: SubscriptionChangeEvent) => {
          const { id, token } = event.current;
          if (id && token) {
            this.webOneSignal.User.PushSubscription.removeEventListener('change', listener);
            this.sendToken(id, token, accountId, sessionId);
          }
        };
        this.webOneSignal.User.PushSubscription.addEventListener('change', listener);
      }
    }
  }

  private normalizeToken(token: string | null): string | null {
    if (!token) return null;
    // Web SDK returns a full FCM endpoint URL; extract just the registration ID
    if (token.startsWith('https://')) return token.split('/').pop() ?? token;
    return token;
  }

  private sendToken(userId: string | null, pushToken: string | null, accountId: number, sessionId: string): void {
    const fullToken = `${userId}|${this.normalizeToken(pushToken)}|${environment.ONE_SIGNAL_APP_ID}`.replaceAll('"', '');
    localStorage.setItem('fullOneSignalToken', fullToken);
    const serial = this.getUUID();
    this.http.post<unknown>(
      `${environment.API_ENDPOINT}MobileApp/UpdatePushTokenV2?AccountID=${accountId}&SessionID=${sessionId}&PushToken=${fullToken}&Serial=${serial}`,
      {}
    ).subscribe();
  }
}
